import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import { Loader2, LogIn, LogOut, CheckCircle, Users } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Shift-Leader roster panel. Renders inline on a talent booking when the
 * talent is the assigned shift_lead. Shows every co-worker on the same
 * shift and lets the leader manually clock teammates in / out via the
 * existing /api/shift-leader/bookings/{id}/manual-checkin|checkout
 * endpoints (every action is server-side audit-logged).
 */
const ShiftLeaderRosterPanel = ({ bookingId, onRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [roster, setRoster] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/shift-leader/bookings/${bookingId}/roster`);
      setRoster(res.data?.roster || []);
    } catch (e) {
      // Non-fatal — talent may be in a state that disallows roster access
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  const callAction = async (id, action) => {
    setBusyId(id + action);
    try {
      await api.post(`/shift-leader/bookings/${id}/${action}`, {});
      toast.success(action === 'manual-checkin' ? 'Teammate clocked in (flagged for admin review)' : 'Teammate clocked out (flagged for admin review)');
      await load();
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="mt-3 text-xs text-gray-500 flex items-center gap-1.5" data-testid={`sl-roster-loading-${bookingId}`}>
        <Loader2 className="w-3 h-3 animate-spin" /> Loading team roster…
      </div>
    );
  }
  if (!roster.length) return null;

  return (
    <div className="mt-3 border border-cyan-200 bg-cyan-50/50 rounded-lg p-3" data-testid={`sl-roster-${bookingId}`}>
      <div className="text-xs font-semibold text-cyan-900 mb-2 flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5" /> Shift Leader · Team Roster
      </div>
      <div className="space-y-1.5">
        {roster.map((r) => {
          const youAreLead = r.booking_id === bookingId;
          if (youAreLead) return null; // skip self
          return (
            <div key={r.booking_id} className="flex items-center justify-between gap-2 bg-white rounded px-2 py-1.5 border border-gray-100" data-testid={`sl-roster-row-${r.booking_id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-gray-900 truncate">{r.name || r.email}</div>
                <div className="text-[10px] text-gray-500 truncate">
                  {r.assignment_type}{r.clocked_in ? ' · clocked in' : ''}{r.clocked_out ? ' · out' : ''}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {!r.clocked_in && !r.clocked_out && (
                  <Button
                    size="sm"
                    onClick={() => callAction(r.booking_id, 'manual-checkin')}
                    disabled={busyId === r.booking_id + 'manual-checkin'}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white h-7 text-xs px-2"
                    data-testid={`sl-clockin-${r.booking_id}`}
                  >
                    {busyId === r.booking_id + 'manual-checkin'
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <><LogIn className="w-3 h-3 mr-1" /> Clock In</>}
                  </Button>
                )}
                {r.clocked_in && !r.clocked_out && (
                  <Button
                    size="sm"
                    onClick={() => callAction(r.booking_id, 'manual-checkout')}
                    disabled={busyId === r.booking_id + 'manual-checkout'}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs px-2"
                    data-testid={`sl-clockout-${r.booking_id}`}
                  >
                    {busyId === r.booking_id + 'manual-checkout'
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <><LogOut className="w-3 h-3 mr-1" /> Clock Out</>}
                  </Button>
                )}
                {r.clocked_out && (
                  <span className="inline-flex items-center text-[10px] text-emerald-700">
                    <CheckCircle className="w-3 h-3 mr-0.5" /> Done
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ShiftLeaderRosterPanel;
